import { addRole, updateRole } from "api/userManagement";
import {
  setRolesDetailsData,
  setRolesPermissionViewOnly,
  togglePermissionModal,
} from "pages/ManageRoles/roleSlice";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify"; // Make sure you have react-toastify installed and configured
import InputComp from "themes/elements/Input";
import ModalComp from "themes/elements/Modal";
import Toggle from "themes/elements/Toggle";
import ModalFooter from "../modalFooter";

function RoleManagementModal({ data, open, setOpen, isEdit, fetchRoles }) {
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const [roleDetails, setRoleDetails] = useState({
    role_name: "",
    role_description: "",
    role_status_flg: 0,
  });

  const { permissionSettings, permissionsData } = useSelector(
    (state) => state.roles
  );
  useEffect(() => {
    dispatch(setRolesDetailsData(roleDetails));
  }, [roleDetails]);

  useEffect(() => {
    if (open) {
      if (isEdit && data) {
        setRoleDetails({
          role_name: data.role_name || "",
          role_description: data.role_description || "",
          role_status_flg: data.role_status_flg || 0,
          role_id: data?.role_id,
        });
      } else {
        setRoleDetails({
          role_name: "",
          role_description: "",
          role_status_flg: 0,
        });
      }
    }
  }, [open, isEdit, data]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRoleDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleToggleChange = (active) => {
    setRoleDetails((prev) => ({
      ...prev,
      role_status_flg: active ? 1 : 0,
    }));
  };

  const validateDetails = () => {
    if (!roleDetails.role_name.trim() || !roleDetails.role_description.trim()) {
      toast.warning("Both role name and description are required.");
      return false;
    }
    return true;
  };

  const handleSetPermissions = () => {
    if (validateDetails()) {
      dispatch(togglePermissionModal(true));
    }
  };

  function findPermissionsByIds(permissionNames) {
    const permissionsMap = [];
    permissionsData.forEach((section) => {
      section.data.forEach((module) => {
        module.children.forEach((permission) => {
          if (permissionNames.includes(permission.value)) {
            permissionsMap.push({
              permission_id: permission.id,
              module_id: module.id,
            });
          }
        });
      });
    });
    return permissionsMap;
  }

  // Assuming selectedPermissions holds the permission labels
  const roleManagementWithIds = findPermissionsByIds(
    permissionSettings?.roleManagement?.map((perm) => perm)
  );
  const userManagementWithIds = findPermissionsByIds(
    permissionSettings?.userManagement?.map((perm) => perm)
  );
  const newsManagementWithIds = findPermissionsByIds(
    permissionSettings?.newsManagement?.map((perm) => perm)
  );
  const notificationManagementWithIds = findPermissionsByIds(
    permissionSettings?.notificationManagement?.map((perm) => perm)
  );

  const wardManagementWithIds = findPermissionsByIds(
    permissionSettings?.wardManagement?.map((perm) => perm)
  );
  const incidentManagementWithIds = findPermissionsByIds(
    permissionSettings?.incidentManagement?.map((perm) => perm)
  );
  const counsilorManagementWithIds = findPermissionsByIds(
    permissionSettings?.counsilorManagement?.map((perm) => perm)
  );
  const projectManagementWithIds = findPermissionsByIds(
    permissionSettings?.projectManagement?.map((perm) => perm)
  );

  const handleSave = async () => {
    const permissionsWithIds = [
      ...roleManagementWithIds,
      ...userManagementWithIds,
      ...newsManagementWithIds,
      ...notificationManagementWithIds,
      ...wardManagementWithIds,
      ...incidentManagementWithIds,
      ...counsilorManagementWithIds,
      ...projectManagementWithIds,
    ];
    if (permissionsWithIds.length === 0) {
      toast.warning("At least one permission must be selected.");
      return; // Exit if no permissions are selected
    }

    setSaving(true); // Set loading state to true

    const addRoleData = {
      role_name: roleDetails.role_name,
      role_description: roleDetails.role_description,
      role_status_flg: roleDetails.role_status_flg,
      permissions: permissionsWithIds,
    };

    const updateRoleData = {
      role_id: roleDetails?.role_id || 1,
      role_name: roleDetails.role_name,
      role_description: roleDetails.role_description,
      role_status_flg: roleDetails.role_status_flg,
      permissions: permissionsWithIds,
    };

    try {
      let response;
      if (isEdit) {
        response = await updateRole(updateRoleData);
        if (response?.message) {
          toast.success(response.message);
          setSaving(false);
          fetchRoles();
        }
      } else {
        response = await addRole(addRoleData);
        if (response?.message) {
          toast.success(response.message);
          setSaving(false);
          fetchRoles();
        }
      }
      setOpen(false); // Close modal on success
    } catch (error) {
      setSaving(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalComp
      title={isEdit ? "Edit role details" : "Add new role"}
      footer={
        <ModalFooter
          handlePrimaryBtn={() => {
            if (!isEdit) {
              setOpen(false);
              dispatch(setRolesPermissionViewOnly(false));
            } else {
              if (validateDetails()) {
                dispatch(setRolesPermissionViewOnly(false));

                dispatch(togglePermissionModal(true));
                // setOpen(false);
              }
            }
          }}
          handleSecondaryBtn={() =>
            isEdit ? handleSave() : handleSetPermissions()
          }
          secondaryBtnLoading={isEdit ? saving : false}
          primaryBtnVariant="secondary"
          primaryBtnText={isEdit ? "Edit permissions" : "Cancel"}
          secondaryBtnText={isEdit ? "Save" : "Set permissions"}
        />
      }
      open={open}
      setOpen={setOpen}
    >
      <div className="flex gap-4 flex-column">
        <InputComp
          name="role_name"
          value={roleDetails.role_name}
          label="Role name"
          placeholder="Enter role name"
          onChange={handleInputChange}
          maxLength={50}
          showCount={true}
        />
        <InputComp
          name="role_description"
          value={roleDetails.role_description}
          label="Role description"
          placeholder="Enter role description"
          onChange={handleInputChange}
          maxLength={200}
          showCount={true}
        />

        <Toggle
          active={roleDetails.role_status_flg === 1}
          onChange={handleToggleChange}
          label="Active"
        />
      </div>
    </ModalComp>
  );
}

export default RoleManagementModal;
